import React, { createContext, useContext, useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
	calculateProgress,
	calculateStatus,
	getSmartResolutionYear,
	isUpdateAllowed,
	isAddAllowed,
	isDeleteAllowed,
	getCompletedCountForResolution,
	calculateTimeLeft,
} from '../utils/resolutionUtils';

const ResolutionContext = createContext();

export const useResolution = () => {
	const context = useContext(ResolutionContext);
	if (!context) {
		throw new Error('useResolution must be used within a ResolutionProvider');
	}
	return context;
};

export const ResolutionProvider = ({ children, personId }) => {
	const { currentUser } = useAuth();
	const navigate = useNavigate();

	// Person and resolution data
	const [person, setPerson] = useState(null);
	const [resolutionsByYear, setResolutionsByYear] = useState({});
	const [availableYears, setAvailableYears] = useState([]);
	const [activeYear, setActiveYear] = useState(new Date().getFullYear());

	// UI state
	const [message, setMessage] = useState('');
	const [messageType, setMessageType] = useState('');
	const [isUpdating, setIsUpdating] = useState(false);

	// Email preferences
	const [receiveWeeklyStatus, setReceiveWeeklyStatus] = useState(false);
	const [email, setEmail] = useState('');
	const [isAdmin, setIsAdmin] = useState(false);

	// Progress calculations
	const [progress, setProgress] = useState(0);
	const [timeLeft, setTimeLeft] = useState('');

	// Helper functions for notifications
	const showError = (msg) => {
		setMessage(msg);
		setMessageType('error');
		setTimeout(() => setMessage(''), 5000);
	};

	const showSuccess = (msg) => {
		setMessage(msg);
		setMessageType('success');
		setTimeout(() => setMessage(''), 3000);
	};

	const clearMessage = () => setMessage('');

	// Data fetching
	const fetchPersonData = async () => {
		console.log('fetchPersonData called with personId:', personId);
		if (!personId) {
			console.log('No personId provided, returning early');
			return;
		}

		try {
			setIsUpdating(true);
			console.log('Calling getPerson function...');
			const getPersonFunction = httpsCallable(functions, 'getPerson');
			const result = await getPersonFunction({ personId });

			console.log('getPerson result:', result.data);

			// Check if we got person data (matching original Person.js pattern)
			if (result.data.person) {
				const personData = result.data.person;
				console.log('Person data received:', personData);

				// Check if account is claimed but user is not authenticated
				if (personData.authEnabled && !currentUser) {
					showError('Account is claimed. Please login to access.');
					setTimeout(() => navigate('/'), 2000); // Redirect after 2 seconds
					return;
				}

				// Check if user is authenticated but accessing wrong account
				if (personData.authEnabled && currentUser && currentUser.uid !== personId) {
					showError('Access denied. You can only view your own resolutions.');
					setTimeout(() => navigate('/'), 2000); // Redirect after 2 seconds
					return;
				}

				setPerson(personData);

				// Set user preferences (using original field names)
				setReceiveWeeklyStatus(personData.sendWeeklyUpdate || false);
				setEmail(personData.email || '');
				setIsAdmin(personData.isAdmin || false);
			} else {
				console.error('No person data in response:', result.data);
				showError('Person not found');
				setTimeout(() => navigate('/'), 2000); // Redirect after 2 seconds
			}
		} catch (error) {
			console.error('Error fetching person:', error);
			// If person not found, show error
			if (error.code === 'not-found' || error.message.includes('Person not found')) {
				showError('Person not found');
				setTimeout(() => navigate('/'), 2000); // Redirect after 2 seconds
				return;
			}
			showError('Error loading data');
			setTimeout(() => navigate('/'), 2000); // Redirect after 2 seconds
		} finally {
			setIsUpdating(false);
		}
	};

	// Separate function to fetch resolutions (matching original Person.js pattern)
	const fetchResolutions = async () => {
		console.log('fetchResolutions called, person:', person);
		// Don't fetch resolutions if person data isn't loaded yet
		if (!person) return;

		// Don't fetch if account is claimed but user is not authenticated
		if (person.authEnabled && !currentUser) {
			return;
		}

		try {
			setIsUpdating(true);
			console.log('Calling getResolutions function...');
			const getResolutionsFunction = httpsCallable(functions, 'getResolutions');
			const result = await getResolutionsFunction({ personId });

			console.log('getResolutions result:', result.data);

			const resolutionsByYearData = result.data.resolutions;
			console.log('Resolutions by year:', resolutionsByYearData);

			// Set the resolutions grouped by year
			setResolutionsByYear(resolutionsByYearData || {});

			// Extract available years and sort them (biggest year first)
			const years = Object.keys(resolutionsByYearData || {})
				.map(Number)
				.sort((a, b) => b - a);
			setAvailableYears(years);

			// Set active year to current year if it exists, otherwise keep the current activeYear
			const currentYear = new Date().getFullYear();
			if (years.includes(currentYear)) {
				setActiveYear(currentYear);
			} else if (years.length > 0 && !years.includes(activeYear)) {
				// If current year doesn't exist and activeYear is not in available years, set to latest year
				setActiveYear(years[0]);
			}
		} catch (error) {
			console.error('Error fetching resolutions:', error);
			showError('Error loading resolutions');
		} finally {
			setIsUpdating(false);
		}
	};

	// Resolution operations
	const addResolution = async (resolutionData, t) => {
		try {
			setIsUpdating(true);
			const addResolutionFunction = httpsCallable(functions, 'addResolution');
			const result = await addResolutionFunction({
				personId,
				...resolutionData,
			});

			if (result.data.success) {
				showSuccess(t('resolutionAddedSuccessfully'));
				await fetchPersonData(); // Refresh data
				return true;
			} else {
				showError(result.data.error || t('failedToAddResolution'));
				return false;
			}
		} catch (error) {
			console.error('Error adding resolution:', error);
			showError(t('errorAddingResolution'));
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	const updateResolution = async (resolutionId, updatedResolution, t) => {
		try {
			setIsUpdating(true);
			const updateResolutionFunction = httpsCallable(functions, 'updateResolution');
			const result = await updateResolutionFunction({
				personId,
				resolutionId,
				resolution: updatedResolution,
				year: activeYear,
			});

			if (result.data.success) {
				showSuccess(t('resolutionUpdatedSuccessfully'));
				await fetchResolutions(); // Refresh resolution data instead of person data
				return true;
			} else {
				showError(result.data.error || t('failedToUpdateResolution'));
				return false;
			}
		} catch (error) {
			console.error('Error updating resolution:', error);
			showError(t('errorUpdatingResolution'));
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	const deleteResolution = async (resolutionId, t) => {
		try {
			setIsUpdating(true);
			const deleteResolutionFunction = httpsCallable(functions, 'deleteResolution');
			const result = await deleteResolutionFunction({
				personId,
				resolutionId,
				year: activeYear,
			});

			if (result.data.success) {
				showSuccess(t('resolutionDeletedSuccessfully'));
				await fetchPersonData(); // Refresh data
				return true;
			} else {
				showError(result.data.error || t('failedToDeleteResolution'));
				return false;
			}
		} catch (error) {
			console.error('Error deleting resolution:', error);
			showError(t('errorDeletingResolution'));
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	const decryptResolution = async (resolutionId, decryptKey, t) => {
		try {
			setIsUpdating(true);
			const getDecryptedText = httpsCallable(functions, 'getDecryptedText');
			const response = await getDecryptedText({
				personId,
				resolutionId,
				key: decryptKey,
				year: activeYear,
			});
			const decryptedText = response.data.text;

			// Get current resolutions and update the decrypted one
			const currentResolutions = getCurrentResolutions();
			const updatedResolutions = currentResolutions.map((res) =>
				res.id === resolutionId ? { ...res, text: decryptedText } : res
			);

			// Update the resolutionsByYear state
			setResolutionsByYear((prev) => ({
				...prev,
				[activeYear]: calculateStatus(updatedResolutions, t),
			}));

			showSuccess('Resolution decrypted successfully');
			return true;
		} catch (error) {
			console.error('Error decrypting resolution:', error);
			showError(`Error: ${error.message}`);
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	// Email preferences
	const updateEmailPreferences = async (newEmail, weeklyStatus) => {
		try {
			setIsUpdating(true);
			const updatePreferencesFunction = httpsCallable(functions, 'updateEmailPreferences');
			const result = await updatePreferencesFunction({
				personId,
				email: newEmail,
				receiveWeeklyStatus: weeklyStatus,
			});

			if (result.data.success) {
				setEmail(newEmail);
				setReceiveWeeklyStatus(weeklyStatus);
				showSuccess('Email preferences updated!');
				return true;
			} else {
				showError(result.data.error || 'Failed to update preferences');
				return false;
			}
		} catch (error) {
			console.error('Error updating preferences:', error);
			showError('Error updating preferences');
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	// User settings (display name, email preferences, timer preference)
	const updateUserSettings = async (settings, t) => {
		try {
			setIsUpdating(true);
			const updateSettingsFunction = httpsCallable(functions, 'updateUserSettings');
			const result = await updateSettingsFunction({
				personId,
				...settings,
			});

			if (result.data.success) {
				// Update local state
				if (settings.displayName !== undefined) {
					setPerson((prev) => ({ ...prev, displayName: settings.displayName }));
				}
				if (settings.email !== undefined) {
					setEmail(settings.email);
				}
				if (settings.receiveWeeklyStatus !== undefined) {
					setReceiveWeeklyStatus(settings.receiveWeeklyStatus);
				}
				if (settings.timerPreference !== undefined) {
					setPerson((prev) => ({ ...prev, timerPreference: settings.timerPreference }));
				}

				showSuccess(t('settingsUpdatedSuccessfully'));
				return true;
			} else {
				showError(result.data.error || t('failedToUpdateSettings'));
				return false;
			}
		} catch (error) {
			console.error('Error updating settings:', error);
			showError(t('errorUpdatingSettings'));
			return false;
		} finally {
			setIsUpdating(false);
		}
	};

	// Utility functions
	const getCurrentResolutions = () => {
		return resolutionsByYear[activeYear] || [];
	};

	// Calculate progress when data changes (using original calculation method)
	useEffect(() => {
		const currentResolutions = getCurrentResolutions();
		if (currentResolutions.length > 0) {
			// Calculate overall progress as the average of each resolution's progress
			// so each resolution contributes equally (binary counts as one resolution).
			const totalPct = currentResolutions.reduce((acc, res) => {
				const pct = calculateProgress(res) || 0;
				return acc + pct;
			}, 0);
			const calculatedProgress = totalPct / currentResolutions.length;
			setProgress(calculatedProgress);
		} else {
			setProgress(0);
		}
	}, [resolutionsByYear, activeYear]);

	// Calculate time left for the year
	useEffect(() => {
		const updateTimeLeft = () => {
			setTimeLeft(calculateTimeLeft(activeYear));
		};

		updateTimeLeft();
		const interval = setInterval(updateTimeLeft, 1000); // Update every second like PersonOriginal
		return () => clearInterval(interval);
	}, [activeYear]);

	// Initial data fetch
	useEffect(() => {
		fetchPersonData();
	}, [personId]);

	// Fetch resolutions when person data is loaded (matching original Person.js pattern)
	useEffect(() => {
		if (person) {
			fetchResolutions();
		}
	}, [person]);

	const value = {
		// Data
		person,
		resolutionsByYear,
		availableYears,
		activeYear,
		progress,
		timeLeft,

		// UI state
		message,
		messageType,
		isUpdating,

		// Email preferences
		receiveWeeklyStatus,
		email,
		isAdmin,

		// Actions
		setActiveYear,
		addResolution,
		updateResolution,
		deleteResolution,
		decryptResolution,
		updateEmailPreferences,
		updateUserSettings,
		fetchPersonData,
		fetchResolutions,

		// Utilities
		getCurrentResolutions,
		isUpdateAllowed: (resolutionYear) => isUpdateAllowed(resolutionYear, currentUser, person, isAdmin),
		isAddAllowed: () => isAddAllowed(currentUser, person),
		isDeleteAllowed: (resolutionYear) => isDeleteAllowed(currentUser, person, resolutionYear),
		getSmartResolutionYear,
		showError,
		showSuccess,
		clearMessage,
	};

	return <ResolutionContext.Provider value={value}>{children}</ResolutionContext.Provider>;
};
